/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { notifications } from "@/shared/ui/components/basic/Notifications";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@/shared/hooks/use-media-query";
import type { globalErrorType } from "@/context/ErrorContext";
import { ToastMessage } from "./use-toast-message";
import {
  getToastColor,
  getToastPosition,
  getToastMinHeight,
  getToastStyles,
  calculateDuration,
} from "./use-toast-config";
import { processMessageOrError, getToastTitle } from "./use-toast-process";
import type { ToastType, ToastOptions } from "./use-toast-types";

export type { ToastType, ToastOptions } from "./use-toast-types";

/**
 * useToast hook.
 *
 * Provides a reusable hook for displaying toast notifications of different types.
 * Supports Success, Error, Warning, and Info messages with consistent styling and duration.
 * Error toasts can include collapsible technical details.
 *
 * @returns {Object} Object containing toast functions (showSuccess, showError,
 * showWarning, showInfo, showToast).
 *
 * @example
 * ```tsx
 * const { showSuccess, showError, showWarning, showInfo } = useToast();
 * showSuccess('Operation completed successfully');
 * showError(error);
 * ```
 */
export const useToast = () => {
  const { t } = useTranslation('common');
  const isMobile = useMediaQuery('(max-width: 768px)');

  /**
   * Gets the message component for a toast: a rich component when technical
   * details exist, otherwise the plain user message string.
   */
  const getMessageComponent = (
    userMessage: string,
    technicalDetails: string | null,
    type: ToastType,
    toastTitle: string,
  ): string | JSX.Element => {
    return technicalDetails
      ? <ToastMessage message={userMessage} technicalDetails={technicalDetails} type={type} title={toastTitle} />
      : userMessage;
  };

  /**
   * Builds the notifications configuration object for a toast.
   */
  const getToastConfig = (
    type: ToastType,
    userMessage: string,
    technicalDetails: string | null,
    toastTitle: string,
    options?: ToastOptions,
  ) => {
    const minHeight = getToastMinHeight(!!technicalDetails, !!isMobile);
    return {
      color: getToastColor(type),
      title: toastTitle,
      message: getMessageComponent(userMessage, technicalDetails, type, toastTitle),
      autoClose: calculateDuration(userMessage, options?.duration),
      position: getToastPosition(!!isMobile),
      styles: getToastStyles(minHeight, !!isMobile),
    };
  };

  /**
   * Generic function to show a toast notification.
   *
   * @param {ToastType} type - Toast type (success, error, warning, info).
   * @param {string | globalErrorType} messageOrError - Message string or error object.
   * @param {ToastOptions} options - Optional configuration.
   */
  const showToast = (
    type: ToastType,
    messageOrError: string | globalErrorType,
    options?: ToastOptions,
  ): void => {
    const { userMessage, technicalDetails } = processMessageOrError(t, type, messageOrError, options);
    const toastTitle = getToastTitle(t, type, options);
    const config = getToastConfig(type, userMessage, technicalDetails, toastTitle, options);
    notifications.show(config as any);
  };

  /**
   * Shows a success toast notification.
   */
  const showSuccess = (message: string, options?: Omit<ToastOptions, 'technicalDetails'>): void => {
    showToast("success", message, options);
  };

  /**
   * Shows an error toast notification with optional technical details.
   */
  const showError = (messageOrError: string | globalErrorType, options?: ToastOptions): void => {
    showToast("error", messageOrError, options);
  };

  /**
   * Shows a warning toast notification.
   */
  const showWarning = (message: string, options?: Omit<ToastOptions, 'technicalDetails'>): void => {
    showToast("warning", message, options);
  };

  /**
   * Shows an info toast notification.
   */
  const showInfo = (message: string, options?: Omit<ToastOptions, 'technicalDetails'>): void => {
    showToast("info", message, options);
  };

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showToast,
  };
};
